import axios from 'axios';
import {
  db,
  doc,
  setDoc,
  getDoc,
  collection,
  writeBatch,
  getDocs,
} from './../firebaseConfig';

const API_KEY = process.env.REACT_APP_API_KEY;
const BASE_URL = process.env.REACT_APP_BASE_URL;

const symbols = [
    { value: 'AAPL', label: 'AAPL'},
    { value: 'MSFT', label: 'MSFT' }, // Microsoft
    { value: 'GOOGL', label: 'GOOGL' }, // Alphabet (Google)
    { value: 'AMZN', label: 'AMZN' }, // Amazon
    { value: 'TSLA', label: 'TSLA' }, // Tesla
    { value: 'NVDA', label: 'NVDA' }, // NVIDIA
    { value: 'JPM', label: 'JPM' }, // JPMorgan Chase
    { value: 'WMT', label: 'WMT' }, // Walmart
    { value: 'EURUSD', label: 'EURUSD' },
    { value: 'GBPUSD', label: 'GBPUSD' },
    { value: 'USDJPY', label: 'USDJPY' },
    { value: 'AUDUSD', label: 'AUDUSD' },
    { value: 'USDCHF', label: 'USDCHF' },
    { value: 'USDCAD', label: 'USDCAD' },
    { value: 'AUDCAD', label: 'AUDCAD' },
    { value: 'AUDNZD', label: 'AUDNZD' },
    { value: 'NZDUSD', label: 'NZDUSD' },
    { value: 'GBPJPY', label: 'GBPJPY' },
    { value: 'AUDJPY', label: 'AUDJPY' },
    { value: 'EURGBP', label: 'EURGBP' },
    { value: 'EURAUD', label: 'EURAUD' },
    { value: 'GBPAUD', label: 'GBPAUD' },
    { value: 'GBPCHF', label: 'GBPCHF' },
    { value: 'EURCHF', label: 'EURCHF' },
    { value: 'NZDCAD', label: 'NZDCAD' },
    { value: 'NZDJPY', label: 'NZDJPY' },
    { value: 'CADJPY', label: 'CADJPY' },
    { value: 'CHFJPY', label: 'CHFJPY' },
    { value: 'XAUUSD', label: 'XAUUSD' },
    { value: 'XAGUSD', label: 'XAGUSD' },
    { value: 'WTIUSD', label: 'WTIUSD' },
    { value: 'BRENTUSD', label: 'BRENTUSD' },
    { value: 'COPPERUSD', label: 'COPPERUSD' },
    { value: 'SUGARUSD', label: 'SUGARUSD' },
    { value: 'NGASUSD', label: 'NGASUSD' },
];

const timeframes = ['1min', '5min', '15min', '30min', '1h', '4h', '1day'];

export const mapIntervalToAPI = (interval) => {
    const mapping = {
        '1': '1min',
        '5': '5min',
        '15': '15min',
        '30': '30min',
        '45': '45min',
        '60': '1h',
        '120': '2h',
        '240': '4h',
        '480': '8h',
        '1D': '1day',
        'W': '1week',
        'M': '1month',
    };
    return mapping[interval] || '15min';
};

export const isDataStaleOrMissing = (lastUpdated) => {
    if (!lastUpdated) return true;
    const lastUpdateTime = new Date(lastUpdated).getTime();
    const currentTime = new Date().getTime();
    const oneDay = 24 * 60 * 60 * 1000;
    return (currentTime - lastUpdateTime) > oneDay;
};

const formatDate = (timestamp) => {
    const timestampNum = Number(timestamp);
    if (isNaN(timestampNum)) {
        console.error('Invalid timestamp:', timestamp);
        return null;
    }
    const date = new Date(timestampNum);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const validateAndFormatData = (marketData) => {
    return marketData
        .filter(bar => 
            bar && 
            typeof bar.time === 'number' && 
            typeof bar.open === 'number' && 
            typeof bar.high === 'number' && 
            typeof bar.low === 'number' && 
            typeof bar.close === 'number'
        )
        .map(bar => ({
            time: bar.time,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
            volume: bar.volume ? bar.volume : 0,
        }));
};

const getIntervalInMillis = (interval) => {
    const intervals = {
        '1': 60 * 1000,
        '5': 5 * 60 * 1000,
        '15': 15 * 60 * 1000,
        '30': 30 * 60 * 1000,
        '60': 60 * 60 * 1000,
        '120': 120 * 60 * 1000,
        '240': 240 * 60 * 1000,
        '1D': 24 * 60 * 60 * 1000,
        '1min': 60 * 1000,
        '5min': 5 * 60 * 1000,
        '15min': 15 * 60 * 1000,
        '30min': 30 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '4h': 240 * 60 * 1000,
        '1day': 24 * 60 * 60 * 1000,
    };
    return intervals[interval] || 15 * 60 * 1000;
};

export const fetchMarketData = async (symbol, interval, startDateMillis, endDateMillis) => {
  console.log(`Fetching market data for ${symbol}, interval: ${interval}, from ${new Date(startDateMillis).toISOString()} to ${new Date(endDateMillis).toISOString()}`);
  
  const startDateAsNumber = Number(startDateMillis);
  const endDateAsNumber = Number(endDateMillis);
  if (isNaN(startDateAsNumber) || isNaN(endDateAsNumber)) {
    console.error('Invalid date parameters:', { startDateMillis, endDateMillis });
    return [];
  }

  // Handle stocks (no '/') vs. forex/commodities (with '/')
  const isStock = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'JPM', 'WMT'].includes(symbol);
  const formattedSymbol = isStock ? symbol : `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  console.log('Formatted symbol for API:', formattedSymbol);

  const formattedStartDate = formatDate(startDateAsNumber);
  const formattedEndDate = formatDate(endDateAsNumber);
  console.log('Formatted dates:', { formattedStartDate, formattedEndDate });

  try {
    const params = {
      apikey: API_KEY,
      interval,
      symbol: formattedSymbol,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      timezone: 'UTC',
      format: 'JSON',
    };
    console.log('API request params:', params);
    const response = await axios.get(`${BASE_URL}/time_series`, { params });
    console.log('API response status:', response.status, 'Data:', response.data);

    if (response.data.code && response.data.message) {
      console.error('API Error:', response.data.code, response.data.message);
      return [];
    }
    if (!response.data.values) {
      console.error('No values in API response:', response.data);
      return [];
    }

    const data = response.data.values || [];
    console.log(`Fetched ${data.length} bars`, data.slice(0, 2)); // Log first 2 bars for inspection

    return data.map(bar => {

      const barDate = new Date(bar.datetime + 'Z');
      return {
        time: Math.floor(barDate.getTime() / 1000),
        open: parseFloat(bar.open),
        high: parseFloat(bar.high),
        low: parseFloat(bar.low),
        close: parseFloat(bar.close),
        volume: bar.volume ? parseFloat(bar.volume) : 0,
      };
    });
  } catch (error) {
    console.error('Error fetching market data:', error.response?.data || error.message);
    return [];
  }
};

export const fetchAndStoreMarketData = async (userID, sessionId, symbol, selectedInterval = '240', startDateMillis, endDateMillis, countBack = null) => {
    const apiInterval = mapIntervalToAPI(selectedInterval); // Define at top scope
  
    console.log('fetchAndStoreMarketData called with:', {
      userID,
      sessionId,
      symbol,
      selectedInterval,
      startDateMillis: startDateMillis ? new Date(startDateMillis).toISOString() : 'invalid',
      endDateMillis: endDateMillis ? new Date(endDateMillis).toISOString() : 'invalid',
      countBack,
    });
  
    let startDateAsNumber = Number(startDateMillis);
    let endDateAsNumber = Number(endDateMillis) || Date.now();
  
    if (isNaN(startDateAsNumber) || isNaN(endDateAsNumber)) {
      console.error('Invalid date parameters:', { startDateMillis, endDateMillis });
      throw new Error('Invalid start or end date');
    }
  
    const symbolRef = doc(db, 'Users', userID, 'backtesting', sessionId);
    const intervalRef = doc(symbolRef, `intervals/${selectedInterval}`);
    const barsCollectionRef = collection(intervalRef, 'bars');
  
    const intervalDoc = await getDoc(intervalRef);
    if (!intervalDoc.exists() || isDataStaleOrMissing(intervalDoc.data()?.lastUpdated)) {
      const intervalMillis = getIntervalInMillis(selectedInterval);
      const rangeMillis = endDateAsNumber - startDateAsNumber;
      const barsInRange = Math.ceil(rangeMillis / intervalMillis);
      const barsNeeded = countBack ? Math.max(countBack, barsInRange + 1000) : barsInRange + 1000;
      const adjustedStartDateMillis = startDateAsNumber - (1000 * intervalMillis);
      console.log(`Calculated bars needed: ${barsNeeded} (range: ${barsInRange}, +1000 historical)`);
  
      const MAX_BARS_PER_REQUEST = 5000;
      const totalRequests = Math.ceil(barsNeeded / MAX_BARS_PER_REQUEST);
      let allMarketData = [];
  
      for (let i = 0; i < totalRequests; i++) {
        const chunkStartMillis = adjustedStartDateMillis + (i * MAX_BARS_PER_REQUEST * intervalMillis);
        const chunkEndMillis = Math.min(chunkStartMillis + (MAX_BARS_PER_REQUEST * intervalMillis), endDateAsNumber);
        console.log(`Fetching chunk ${i + 1}/${totalRequests}: ${new Date(chunkStartMillis).toISOString()} to ${new Date(chunkEndMillis).toISOString()}`);
  
        const chunkData = await fetchMarketData(symbol, apiInterval, chunkStartMillis, chunkEndMillis);
        console.log(`Chunk ${i + 1} fetched: ${chunkData.length} bars, First: ${new Date(chunkData[0].time * 1000).toISOString()}, Last: ${new Date(chunkData[chunkData.length - 1].time * 1000).toISOString()}`);
        
        allMarketData = [...allMarketData, ...chunkData];
  
        if (chunkEndMillis >= endDateAsNumber && chunkData.length === 0) break;
      }
  
      if (allMarketData.length > 0) {
        const validatedData = validateAndFormatData(allMarketData);
        const filteredData = validatedData.filter(bar => bar.time * 1000 >= adjustedStartDateMillis && bar.time * 1000 <= endDateAsNumber);
        console.log(`Filtered data from ${new Date(adjustedStartDateMillis).toISOString()} to ${new Date(endDateAsNumber).toISOString()}`);
        console.log(`Total bars after filtering: ${filteredData.length}`);
  
        const MAX_BYTES_PER_CHUNK = 800 * 1024;
        const avgBarSize = JSON.stringify(filteredData[0]).length;
        const estBarsPerChunk = Math.floor(MAX_BYTES_PER_CHUNK / avgBarSize);
        const CHUNK_SIZE = Math.max(1, Math.min(5000, estBarsPerChunk));
        console.log(`Estimated bar size: ${avgBarSize} bytes, chunk size: ${CHUNK_SIZE} bars`);
  
        const chunks = [];
        for (let i = 0; i < filteredData.length; i += CHUNK_SIZE) {
          chunks.push(filteredData.slice(i, i + CHUNK_SIZE));
        }
  
        const BATCH_SIZE = 400;
        for (let i = 0; i < chunks.length; i += BATCH_SIZE) {
          const batch = writeBatch(db);
          const batchChunks = chunks.slice(i, i + BATCH_SIZE);
          batchChunks.forEach((chunk, idx) => {
            const chunkRef = doc(barsCollectionRef, `chunk_${i + idx}`);
            batch.set(chunkRef, { data: chunk });
          });
          if (i + BATCH_SIZE >= chunks.length) {
            batch.set(intervalRef, {
              lastUpdated: new Date().toISOString(),
              totalBars: filteredData.length,
              chunkCount: chunks.length,
              chunkSize: CHUNK_SIZE,
            });
          }
          await batch.commit();
          console.log(`Committed batch ${i / BATCH_SIZE + 1}: ${batchChunks.length} chunks`);
        }
  
        console.log(`Stored ${filteredData.length} bars across ${chunks.length} chunks for ${symbol} (${selectedInterval})`);
        return filteredData;
      } else {
        console.log(`No data fetched for ${symbol} (${selectedInterval})`);
        return [];
      }
    } else {
      const barsSnapshot = await getDocs(barsCollectionRef);
      const existingData = [];
      barsSnapshot.forEach(doc => existingData.push(...doc.data().data));
      console.log(`Using cached data: ${existingData.length} bars`);
      return existingData.sort((a, b) => a.time - b.time);
    }
  };